import { clampDate, intervalFromInclusiveDates } from "@/utils/date"
import { DateTime, type DateTimeMaybeValid, Interval } from "luxon"

export {
  isCompleteInclusivePickerRange,
  pickerBoundsForPartialRange,
} from "@/utils/date"

const INTERVAL_GRANULARITIES = ["as_given", "day", "month", "year"] as const

interface CompleteInclusivePickerRange {
  end: DateTime
  start: DateTime
}

const toStartOffsetForInclusiveDays = (days: number): { days: number } => ({
  days: days - 1,
})

const PAST_7_DAYS_START_OFFSET = toStartOffsetForInclusiveDays(7)

export const INTERVAL_DATE_RANGE_SHORTCUTS = [
  { key: "past3Days", startOffset: toStartOffsetForInclusiveDays(3) },
  { key: "past7Days", startOffset: PAST_7_DAYS_START_OFFSET },
  { key: "past1Month", startOffset: { months: 1 } },
  { key: "past3Months", startOffset: { months: 3 } },
  { key: "past1Year", startOffset: { years: 1 } },
] as const

type IntervalDateRangeShortcutStartOffset =
  (typeof INTERVAL_DATE_RANGE_SHORTCUTS)[number]["startOffset"]

interface InclusiveShortcutPickerRange extends CompleteInclusivePickerRange {
  disabled: boolean
}

interface InclusiveShortcutPickerRangeOptions {
  maxDate: DateTime | null | undefined
  minDate: DateTime | null | undefined
  now?: DateTimeMaybeValid
  startOffset: IntervalDateRangeShortcutStartOffset
}

const inclusiveShortcutRangeOverlapsBounds = (
  range: CompleteInclusivePickerRange,
  minDate: DateTime | null | undefined,
  maxDate: DateTime | null | undefined
): boolean => {
  if (minDate?.isValid && range.end < minDate.startOf("day")) {
    return false
  }
  if (maxDate?.isValid && range.start > maxDate.startOf("day")) {
    return false
  }
  return true
}

export const toInclusiveShortcutPickerRange = ({
  maxDate,
  minDate,
  now = DateTime.now(),
  startOffset,
}: InclusiveShortcutPickerRangeOptions): InclusiveShortcutPickerRange => {
  const end = (maxDate?.isValid ? maxDate : now).startOf("day")
  const start = end.minus(startOffset)
  if (!inclusiveShortcutRangeOverlapsBounds({ end, start }, minDate, maxDate)) {
    return { disabled: true, end, start }
  }

  return {
    disabled: false,
    end: clampDate(end, minDate, maxDate),
    start: clampDate(start, minDate, maxDate),
  }
}

export type IntervalGranularity = (typeof INTERVAL_GRANULARITIES)[number]

const INTERVAL_GRANULARITY_VALUES: ReadonlySet<string> = new Set(
  INTERVAL_GRANULARITIES
)

export const INTERVAL_GRANULARITY_PARAM = "granularity"

const INTERVAL_RESAMPLE: Record<
  Exclude<IntervalGranularity, "as_given">,
  string
> = {
  day: "1d",
  month: "1mo",
  year: "1y",
}

export const isIntervalGranularity = (
  value: string | null
): value is IntervalGranularity =>
  value != null && INTERVAL_GRANULARITY_VALUES.has(value)

export const defaultGranularity = (): IntervalGranularity => "as_given"

export const intervalDateRangeBounds = (
  now: DateTimeMaybeValid = DateTime.now()
): Interval =>
  Interval.fromDateTimes(
    now.minus({ years: 10 }).startOf("day"),
    now.plus({ days: 1 }).startOf("day")
  )

interface DefaultIntervalInclusiveEndOptions {
  latestStartDatetime?: string | null
  now?: DateTimeMaybeValid
  timeZone: string
}

export const defaultIntervalInclusiveEnd = ({
  latestStartDatetime,
  now = DateTime.now(),
  timeZone,
}: DefaultIntervalInclusiveEndOptions): DateTime => {
  const siteNow = now.setZone(timeZone)
  const today = (siteNow.isValid ? siteNow : now).startOf("day")
  const latestDataDate = siteLocalDay(latestStartDatetime, timeZone)
  if (!latestDataDate?.isValid) {
    return today
  }

  return DateTime.min(latestDataDate, today)
}

export const defaultIntervalDateRange = (
  inclusiveEnd: DateTimeMaybeValid = DateTime.now()
): Interval => {
  const end = inclusiveEnd.startOf("day")
  return intervalFromInclusiveDates(end.minus(PAST_7_DAYS_START_OFFSET), end)
}

export const siteLocalDay = (
  iso: string | null | undefined,
  timeZone: string | null | undefined
): DateTime | null => {
  if (!iso || !timeZone) {
    return null
  }

  const local = DateTime.fromISO(iso, { zone: timeZone }).startOf("day")
  return local.isValid ? local : null
}

export const siteMidnightUtcIso = (
  dateTime: DateTime | null | undefined,
  timeZone: string | null | undefined
): string | undefined => {
  const date = dateTime?.toISODate()
  if (!date || !timeZone) {
    return undefined
  }

  const localMidnight = DateTime.fromISO(date, { zone: timeZone }).startOf(
    "day"
  )
  if (!localMidnight.isValid) {
    return undefined
  }

  return (
    localMidnight.toUTC().toISO({ suppressMilliseconds: true }) ?? undefined
  )
}

const AS_GIVEN_INTERVAL_MINUTES = 30
const MAX_AS_GIVEN_INCLUSIVE_DAYS = 366

export const INTERVAL_CHART_MAX_DATA_POINTS =
  MAX_AS_GIVEN_INCLUSIVE_DAYS * 24 * (60 / AS_GIVEN_INTERVAL_MINUTES)

const estimatedChartPointCount = (
  dateRange: Interval,
  granularity: IntervalGranularity
): number => {
  if (!dateRange.isValid) {
    return 0
  }

  if (granularity !== "as_given") {
    return dateRange.count(granularity)
  }

  return Math.ceil(dateRange.length("minutes") / AS_GIVEN_INTERVAL_MINUTES)
}

export const isIntervalChartWindowTooDense = (
  dateRange: Interval,
  granularity: IntervalGranularity
): boolean =>
  estimatedChartPointCount(dateRange, granularity) >
  INTERVAL_CHART_MAX_DATA_POINTS

export const resampleForGranularity = (
  granularity: IntervalGranularity
): string | undefined =>
  granularity === "as_given" ? undefined : INTERVAL_RESAMPLE[granularity]
